/**
 * Price book — types, loader และ exporter (pure module: ไม่ import interface/controller เพื่อกัน cycle)
 *
 * ไฟล์ข้อมูลอยู่ที่ src/react-lib/apps/MeGen/aiPriceBook.json — แก้ราคาที่นั่น ไม่ต้องแตะ source code
 * loader ต้องไม่ throw ไม่ว่าไฟล์จะผิดรูปแบบอย่างไร: รายการที่ผิดถูกข้ามและรายงานใน `issues`
 * exporter = ทางกลับ: รวมราคาที่แก้ในหน้าจอ (override) กลับเป็น JSON ให้วางทับไฟล์ได้
 */

import { usageOverrideTargetOf } from "./aiUsageModels";

export type AiResolutionBucket = "480P" | "720P" | "1080P";
export type AiRateUnit = "second" | "image" | "token_1m" | "gpu_hour" | "call";
export type AiRateDim = "resolution" | "audio";

export const RESOLUTION_BUCKETS: AiResolutionBucket[] = ["480P", "720P", "1080P"];
const AI_RATE_UNITS: AiRateUnit[] = ["second", "image", "token_1m", "gpu_hour", "call"];
export const PRICE_BOOK_PATH = "src/react-lib/apps/MeGen/aiPriceBook.json";
const PROVIDER_DEFAULT_MODEL = "*"; // key override ของค่าเริ่มต้นราย provider = `${provider}|*`

export type RateOverrides = Record<string, Record<string, number>>; // key → variant → USD

export type AiModelRate = {
  key: string; // `${provider}|${model}` (lower-case) — ใช้ร่วมกันทุกช่วงวันที่ของ model เดียวกัน
  provider: string;
  model: string;
  service: string; // video | image | tts | llm ("" = ไม่ระบุ → ใช้จาก usage)
  unit: AiRateUnit;
  prices: Record<string, number>; // variant → USD (variant ที่ไม่มี = ยังไม่ได้ตั้งราคา)
  tiers?: AiTokenTier[]; // unit=token_1m เท่านั้น: ชั้นราคาตาม input tokens ต่อ call — prices บนสุด = ชั้นแรก (ใช้ตอนไม่รู้ token)
  startDate?: string; // YYYY-MM-DD (inclusive) — ไม่ระบุ = ใช้ได้ตั้งแต่ไหนก็ได้
  endDate?: string; // YYYY-MM-DD (inclusive) — ไม่ระบุ = ไม่มีวันหมด; 2499-12-31 = ถาวร (OPEN_END_DATE)
  dims?: AiRateDim[]; // unit=second เท่านั้น; [] = ราคาเดียวทุก resolution
  defaultResolution?: AiResolutionBucket;
  free?: boolean; // ราคา 0 = "ฟรี" ไม่ใช่ "ยังไม่ได้ตั้งราคา"
  inferred?: boolean; // ไม่มีใน price book → มาจาก provider_defaults หรือเดาจาก usage
  source?: string; // URL เอกสารราคา
  note?: string;
};

/** ชั้นราคา token ต่อ call (เช่น qwen-plus ≤256K ถูกกว่า >256K) — เลือกตาม input tokens ของแถวนั้น */
export type AiTokenTier = {
  maxInputTokens?: number; // ขอบบน input tokens ต่อ call (inclusive); ไม่ระบุ = ชั้นปิดท้าย (catch-all)
  prices: Record<string, number>; // { in, out } ต้องครบ
};

export type ProviderDefault = {
  provider: string;
  free: boolean;
  unit?: AiRateUnit; // ไม่ระบุ = เดาจาก usage
  service?: string;
  dims?: AiRateDim[];
  defaultResolution?: AiResolutionBucket;
  prices: Record<string, number>;
  note?: string;
};

export type PriceBook = {
  updated: string | null;
  models: AiModelRate[];
  providerDefaults: Record<string, ProviderDefault>; // provider (lower-case) → default
};

export type PriceBookLoadResult = { book: PriceBook; issues: string[] };

export const EMPTY_PRICE_BOOK: PriceBook = { updated: null, models: [], providerDefaults: {} };

export const rateKeyOf = (provider: string | null | undefined, model: string | null | undefined): string =>
  `${(provider || "").toLowerCase()}|${(model || "").toLowerCase()}`;

export const providerDefaultKeyOf = (provider: string | null | undefined): string =>
  rateKeyOf(provider, PROVIDER_DEFAULT_MODEL);

const isProviderDefaultKey = (key: string): boolean => key.endsWith(`|${PROVIDER_DEFAULT_MODEL}`);

// ─── ช่วงวันที่ (start/end date) ──────────────────────────────────────────────

/** วันหมดอายุที่ใช้แทน "ไม่รู้วันหมด / ถาวร" — เลือกเป็นปีที่แน่นอนว่าไกลกว่าราคา AI โมเดลไหน */
export const OPEN_END_DATE = "2499-12-31";

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

export const isValidDay = (v: unknown): v is string => typeof v === "string" && DAY_RE.test(v);

/**
 * ISO datetime / "YYYY-MM-DD" → วันตามปฏิทินท้องถิ่น (YYYY-MM-DD) — หน้าต่างวันที่ใน price book
 * เป็นวันตามปฏิทินที่คนเขียนไฟล์กรอก จึงต้องแปลงเป็นวันท้องถิ่นก่อนเทียบ ไม่ใช่ตัด 10 ตัวแรกของ ISO
 * (คนละ timezone อาจคนละวัน) อ่านไม่ออก → null
 */
export const localDayOf = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  if (DAY_RE.test(iso)) return iso;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** วันนี้ (ท้องถิ่น) แบบ YYYY-MM-DD — ใช้เมื่อแถว usage ไม่มีวันที่ (เช่น aggregate ของ by_model) */
export const todayDay = (): string => localDayOf(new Date().toISOString()) || "";

/** วัน `day` (YYYY-MM-DD) อยู่ในหน้าต่างของรายการนี้หรือไม่ (รายการไม่มีวันที่ = ครอบทุกวัน) */
export const windowContains = (rate: { startDate?: string; endDate?: string }, day: string): boolean =>
  (rate.startDate === undefined || day >= rate.startDate) && (rate.endDate === undefined || day <= rate.endDate);

/**
 * key ของ override รายการนี้ — รายการที่มีวันที่ต่อท้ายด้วย `@start~end` เพื่อให้แก้ราคาแต่ละช่วงแยกกัน
 * (แก้ราคาช่วง introductory แล้วต้องไม่รั่วไปช่วงถัดไป); รายการไม่มีวันที่ใช้ key เดิม (ข้อมูลเก่าใช้ได้ทันที)
 */
export const entryKeyOf = (rate: { key: string; startDate?: string; endDate?: string }): string =>
  rate.startDate === undefined && rate.endDate === undefined
    ? rate.key
    : `${rate.key}@${rate.startDate || ""}~${rate.endDate || ""}`;

// ─── Variants ────────────────────────────────────────────────────────────────

// variant key ที่ถูกต้องของแต่ละ unit (กัน typo ใน price book)
export const isVariantKeyForUnit = (unit: AiRateUnit, key: string): boolean => {
  if (unit === "second") return /^(all|480P|720P|1080P)(\/noaudio)?$/.test(key);
  if (unit === "image") return key === "image";
  if (unit === "token_1m") return key === "in" || key === "out";
  if (unit === "gpu_hour") return key === "hour";
  return key === "call";
};

// "1080p" → "1080P", "ALL/NOAUDIO" → "all/noaudio"
const normalizeVariantKey = (key: string): string =>
  key
    .trim()
    .replace(/^(\d{3,4})p/, "$1P")
    .replace(/^all/i, "all")
    .replace(/\/noaudio$/i, "/noaudio");

const VARIANT_ORDER: Record<string, number> = { all: 0, "480P": 1, "720P": 2, "1080P": 3, image: 0, in: 0, out: 1, hour: 0, call: 0 };

// ลำดับแสดงผล: all < 480P < 720P < 1080P, มีเสียงก่อนไม่มีเสียง, in ก่อน out
export const variantSortKey = (variant: string): number => {
  const [res, flag] = variant.split("/");
  const base = VARIANT_ORDER[res];
  return (base === undefined ? 9 : base) * 2 + (flag ? 1 : 0);
};

// variant ที่ควรมีตาม unit/dims — ใช้เมื่อ price book ยังไม่มีราคา (null) และตอน export
export const expectedVariantsOf = (unit: AiRateUnit, dims?: AiRateDim[]): string[] => {
  if (unit === "image") return ["image"];
  if (unit === "token_1m") return ["in", "out"];
  if (unit === "gpu_hour") return ["hour"];
  if (unit === "call") return ["call"];
  const d = dims || [];
  const base = d.includes("resolution") ? ["720P", "1080P"] : ["all"];
  // จัดกลุ่มตาม resolution: 720P, 720P/noaudio, 1080P, 1080P/noaudio (ตรงกับ variantSortKey)
  return d.includes("audio") ? base.reduce<string[]>((acc, b) => acc.concat([b, `${b}/noaudio`]), []) : base;
};

// dims ที่ key ของ prices บ่งบอก (unit=second)
const dimsFromKeys = (keys: string[]): AiRateDim[] => {
  const dims: AiRateDim[] = [];
  if (keys.some((k) => /^\d{3,4}P/.test(k))) dims.push("resolution");
  if (keys.some((k) => k.endsWith("/noaudio"))) dims.push("audio");
  return dims;
};

// unit ที่เข้ากับ variant ทั้งหมด (key ของแต่ละ unit ไม่ซ้ำกัน) — null = ว่าง/ปนกัน
export const unitFromVariants = (variants: string[]): AiRateUnit | null =>
  (variants.length && AI_RATE_UNITS.find((u) => variants.every((v) => isVariantKeyForUnit(u, v)))) || null;

// ─── Validation helpers ──────────────────────────────────────────────────────

const KNOWN_ROOT_KEYS = ["updated", "currency", "provider_defaults", "models"];
const KNOWN_ENTRY_KEYS = [
  "provider",
  "model",
  "service",
  "unit",
  "prices",
  "tiers",
  "start_date",
  "end_date",
  "dims",
  "default_resolution",
  "free",
  "source",
  "note",
];
const KNOWN_DEFAULT_KEYS = ["service", "unit", "prices", "dims", "default_resolution", "free", "note"];
const KNOWN_TIER_KEYS = ["max_input_tokens", "prices"];

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const isUnit = (v: unknown): v is AiRateUnit => typeof v === "string" && (AI_RATE_UNITS as string[]).includes(v);
const isBucket = (v: unknown): v is AiResolutionBucket =>
  typeof v === "string" && (RESOLUTION_BUCKETS as string[]).includes(v);
const isDim = (v: unknown): v is AiRateDim => v === "resolution" || v === "audio";
const nonEmptyString = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);

type NormalizedPrices = { prices: Record<string, number>; keys: string[] }; // keys = key ที่ถูกต้องของ unit (รวมที่เป็น null)

const normalizePrices = (
  prices: unknown,
  unit: AiRateUnit | undefined,
  where: string,
  issues: string[]
): NormalizedPrices => {
  const out: Record<string, number> = {};
  const keys: string[] = [];
  if (!isRecord(prices)) {
    issues.push(`${where}: prices ต้องเป็น object เช่น { "1080P": 0.15 }`);
    return { prices: out, keys };
  }

  Object.entries(prices).forEach(([rawKey, value]) => {
    const key = normalizeVariantKey(rawKey);
    if (unit && !isVariantKeyForUnit(unit, key)) {
      issues.push(`${where}: prices.${rawKey} ไม่ใช่ key ของ unit ${unit} (ข้าม)`);
      return;
    }
    keys.push(key);
    if (value === null) return; // ยังไม่ได้ตั้งราคา

    let n: number | null = null;
    if (typeof value === "number") {
      n = value;
    } else if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
      n = Number(value);
      issues.push(`${where}: prices.${rawKey} เป็นข้อความ "${value}" → ใช้เป็นตัวเลข ${n} (ควรใส่เป็นตัวเลขไม่มีเครื่องหมายคำพูด)`);
    }
    if (n === null || !Number.isFinite(n) || n < 0) {
      issues.push(`${where}: prices.${rawKey} ค่า "${String(value)}" ไม่ถูกต้อง (ต้องเป็นตัวเลข ≥ 0 หรือ null) → ถือว่ายังไม่ได้ตั้งราคา`);
      return;
    }
    out[key] = n;
  });

  return { prices: out, keys };
};

const reportUnknownKeys = (obj: Record<string, unknown>, known: string[], where: string, issues: string[]) => {
  Object.keys(obj)
    .filter((k) => !k.startsWith("_") && !known.includes(k))
    .forEach((k) => issues.push(`${where}: key "${k}" ไม่รู้จัก (ใช้ได้: ${known.join(", ")})`));
};

const parseDims = (raw: unknown, unit: AiRateUnit, where: string, issues: string[]): AiRateDim[] | undefined => {
  if (raw === undefined) return undefined;
  if (unit !== "second") {
    issues.push(`${where}: dims ใช้ได้เฉพาะ unit second (เมิน)`);
    return undefined;
  }
  if (!Array.isArray(raw)) {
    issues.push(`${where}: dims ต้องเป็น array เช่น ["resolution"] (เมิน)`);
    return undefined;
  }
  const bad = raw.filter((d) => !isDim(d));
  if (bad.length) issues.push(`${where}: dims มีค่าที่ไม่รู้จัก ${bad.map(String).join(", ")} (ใช้ได้: resolution, audio)`);
  return raw.filter(isDim);
};

// unit=second: dims กับ key ของ prices ต้องสอดคล้องกัน — ไม่มี dims → อนุมานจาก key (F1)
const reconcileDims = (
  explicit: AiRateDim[] | undefined,
  keys: string[],
  where: string,
  issues: string[]
): AiRateDim[] => {
  const inferred = dimsFromKeys(keys);
  if (explicit === undefined) {
    if (inferred.length) issues.push(`${where}: ไม่มี dims → อนุมานจาก prices เป็น [${inferred.join(", ")}] (ควรใส่ "dims" ให้ชัด)`);
    return inferred;
  }
  if (inferred.includes("resolution") && !explicit.includes("resolution")) {
    issues.push(`${where}: prices มี 480P/720P/1080P แต่ dims ไม่มี resolution → ราคาเหล่านั้นจะไม่ถูกใช้ (ใส่ "dims": ["resolution"])`);
  }
  if (inferred.includes("audio") && !explicit.includes("audio")) {
    issues.push(`${where}: prices มี /noaudio แต่ dims ไม่มี audio → ราคาปิดเสียงจะไม่ถูกใช้ (ใส่ "audio" ใน dims)`);
  }
  if (explicit.includes("resolution") && !inferred.includes("resolution") && !keys.includes("all")) {
    issues.push(`${where}: dims มี resolution แต่ prices ไม่มี 720P/1080P (หรือ all)`);
  }
  if (explicit.includes("audio") && !inferred.includes("audio")) {
    issues.push(`${where}: dims มี audio แต่ prices ไม่มี key /noaudio → ปิดเสียงจะคิดราคามีเสียง`);
  }
  return explicit;
};

const parseDefaultResolution = (raw: unknown, where: string, issues: string[]): AiResolutionBucket | undefined => {
  if (raw === undefined) return undefined;
  const bucket = typeof raw === "string" ? raw.toUpperCase() : raw;
  if (isBucket(bucket)) return bucket;
  issues.push(`${where}: default_resolution "${String(raw)}" ไม่รู้จัก (ใช้ได้: ${RESOLUTION_BUCKETS.join(", ")}) (เมิน)`);
  return undefined;
};

// ชั้นราคา token ต่อ call (unit=token_1m เท่านั้น) — ชั้นที่พังถูกข้ามรายชั้นพร้อม issue;
// ทั้งก้อนถูกเมินเมื่อ unit ไม่ใช่ token_1m หรือไม่เหลือชั้นที่ใช้ได้เลย
const parseTiers = (raw: unknown, unit: AiRateUnit, where: string, issues: string[]): AiTokenTier[] | undefined => {
  if (raw === undefined) return undefined;
  if (unit !== "token_1m") {
    issues.push(`${where}: tiers ใช้ได้เฉพาะ unit token_1m (เมิน)`);
    return undefined;
  }
  if (!Array.isArray(raw) || !raw.length) {
    issues.push(`${where}: tiers ต้องเป็น array ที่ไม่ว่าง เช่น [{"max_input_tokens": 256000, "prices": {"in": 0.4, "out": 1.6}}] (เมิน)`);
    return undefined;
  }

  const tiers: AiTokenTier[] = [];
  raw.forEach((t, i) => {
    const twhere = `${where}.tiers[${i}]`;
    if (!isRecord(t)) {
      issues.push(`${twhere}: ต้องเป็น object (ข้ามชั้นนี้)`);
      return;
    }
    reportUnknownKeys(t, KNOWN_TIER_KEYS, twhere, issues);

    let maxInputTokens: number | undefined;
    if (t.max_input_tokens !== undefined) {
      const n = Number(t.max_input_tokens);
      if (!Number.isFinite(n) || n <= 0) {
        issues.push(`${twhere}: max_input_tokens "${String(t.max_input_tokens)}" ต้องเป็นตัวเลข > 0 (ข้ามชั้นนี้)`);
        return;
      }
      maxInputTokens = n;
    }
    const normalized = normalizePrices(t.prices, unit, twhere, issues);
    if (normalized.prices.in === undefined || normalized.prices.out === undefined) {
      issues.push(`${twhere}: ต้องมีราคา in และ out ครบ (ข้ามชั้นนี้)`);
      return;
    }
    tiers.push({ ...(maxInputTokens !== undefined ? { maxInputTokens } : {}), prices: normalized.prices });
  });

  if (!tiers.length) {
    issues.push(`${where}: tiers ไม่มีชั้นที่ใช้ได้เลย (เมินทั้งก้อน)`);
    return undefined;
  }
  if (tiers[tiers.length - 1].maxInputTokens !== undefined) {
    issues.push(`${where}: ชั้นสุดท้ายมี max_input_tokens → call ที่ input ยาวกว่าทุกชั้นจะตกมาใช้ชั้นสุดท้ายอยู่ดี (ควรปิดท้ายด้วยชั้นที่ไม่มีขอบเขต)`);
  }
  return tiers;
};

// start/end date ของ 1 รายการ — รูปแบบผิดหรือ end ก่อน start → ข้ามรายการทั้งแถว (คืน null)
// เพราะถ้าเมินแค่วันที่ รายการจะกลายเป็น "ไม่มีวันที่" ที่ครอบทุกวัน แล้วซ้อนกับช่วงอื่นของ model เดียวกันแบบเงียบ ๆ
const parseDateWindow = (
  m: Record<string, unknown>,
  where: string,
  issues: string[]
): { startDate?: string; endDate?: string } | null => {
  let startDate: string | undefined;
  let endDate: string | undefined;
  if (m.start_date !== undefined) {
    if (isValidDay(m.start_date)) startDate = m.start_date;
    else {
      issues.push(`${where}: start_date "${String(m.start_date)}" ต้องเป็นรูปแบบ YYYY-MM-DD (ข้ามรายการนี้)`);
      return null;
    }
  }
  if (m.end_date !== undefined) {
    if (isValidDay(m.end_date)) endDate = m.end_date;
    else {
      issues.push(`${where}: end_date "${String(m.end_date)}" ต้องเป็นรูปแบบ YYYY-MM-DD (ข้ามรายการนี้)`);
      return null;
    }
  }
  if (startDate && endDate && endDate < startDate) {
    issues.push(`${where}: end_date ${endDate} มาก่อน start_date ${startDate} (ข้ามรายการนี้)`);
    return null;
  }
  return { startDate, endDate };
};

const UNBOUNDED_END = "9999-99-99"; // เทียบเป็น string ได้กับ YYYY-MM-DD ทุกค่า — ใช้เฉพาะตอนเทียบช่วง

// ช่วงซ้อนกันไหม (inclusive ทั้งสองวันปลาย) — รายการไม่มีวันที่ = ครอบทุกวินาที จึงซ้อนกับทุกช่วง
const windowsOverlap = (a: { startDate?: string; endDate?: string }, b: { startDate?: string; endDate?: string }): boolean =>
  (a.startDate || "") <= (b.endDate || UNBOUNDED_END) && (b.startDate || "") <= (a.endDate || UNBOUNDED_END);

// ─── Loader ──────────────────────────────────────────────────────────────────

export const loadPriceBook = (raw: unknown): PriceBookLoadResult => {
  const issues: string[] = [];
  if (!isRecord(raw)) {
    issues.push("price book ต้องเป็น JSON object");
    return { book: EMPTY_PRICE_BOOK, issues };
  }
  reportUnknownKeys(raw, KNOWN_ROOT_KEYS, "price book", issues);
  if (raw.models === undefined) issues.push("price book ไม่มี models → ทุก model จะขึ้นว่ายังไม่ได้ตั้งราคา");

  // updated / currency
  let updated: string | null = null;
  if (raw.updated !== undefined) {
    if (typeof raw.updated === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw.updated)) updated = raw.updated;
    else {
      issues.push(`updated "${String(raw.updated)}" ควรเป็นรูปแบบ YYYY-MM-DD`);
      updated = typeof raw.updated === "string" ? raw.updated : null;
    }
  }
  if (raw.currency !== undefined && raw.currency !== "USD") {
    issues.push(`currency "${String(raw.currency)}" — ระบบคิดเป็น USD เท่านั้น`);
  }

  // provider_defaults
  const providerDefaults: Record<string, ProviderDefault> = {};
  if (raw.provider_defaults !== undefined) {
    if (!isRecord(raw.provider_defaults)) {
      issues.push("provider_defaults ต้องเป็น object");
    } else {
      Object.entries(raw.provider_defaults).forEach(([provider, d]) => {
        const where = `provider_defaults.${provider}`;
        if (!isRecord(d)) {
          issues.push(`${where}: ต้องเป็น object`);
          return;
        }
        reportUnknownKeys(d, KNOWN_DEFAULT_KEYS, where, issues);

        const free = d.free === true;
        let unit: AiRateUnit | undefined;
        if (d.unit !== undefined) {
          if (!isUnit(d.unit)) {
            issues.push(`${where}: unit "${String(d.unit)}" ไม่รู้จัก (ใช้ได้: ${AI_RATE_UNITS.join(", ")}) (ข้าม)`);
            return;
          }
          unit = d.unit;
        }
        if (!free && !unit) {
          issues.push(`${where}: ต้องมี free: true หรือ unit (ข้าม)`);
          return;
        }

        const normalized = d.prices === undefined ? { prices: {}, keys: [] } : normalizePrices(d.prices, unit, where, issues);
        let dims: AiRateDim[] | undefined;
        let defaultResolution: AiResolutionBucket | undefined;
        if (unit === "second") {
          dims = reconcileDims(parseDims(d.dims, unit, where, issues), normalized.keys, where, issues);
          defaultResolution = parseDefaultResolution(d.default_resolution, where, issues);
          if (dims.includes("resolution") && !defaultResolution) defaultResolution = "1080P";
        } else if (d.dims !== undefined || d.default_resolution !== undefined) {
          issues.push(`${where}: dims/default_resolution ใช้ได้เฉพาะ unit second (เมิน)`);
        }

        providerDefaults[provider.toLowerCase()] = {
          provider: provider.toLowerCase(),
          free,
          unit,
          service: nonEmptyString(d.service) || undefined,
          dims,
          defaultResolution,
          prices: normalized.prices,
          note: nonEmptyString(d.note) || undefined,
        };
      });
    }
  }

  // models
  const models: AiModelRate[] = [];
  // ช่วงวันที่ที่รับไปแล้วต่อ key — 1 model ได้หลายรายการถ้าช่วงไม่ซ้อนกัน (เช่น ราคา introductory → ราคาปกติ)
  const windowsByKey = new Map<string, { startDate?: string; endDate?: string }[]>();
  if (raw.models !== undefined && !Array.isArray(raw.models)) {
    issues.push("models ต้องเป็น array");
  } else {
    ((raw.models as unknown[]) || []).forEach((m, i) => {
      const provider = isRecord(m) ? nonEmptyString(m.provider) : null;
      const model = isRecord(m) ? nonEmptyString(m.model) : null;
      const where = `models[${i}] (${provider || "?"}|${model || "?"})`;

      if (!isRecord(m)) {
        issues.push(`${where}: ต้องเป็น object`);
        return;
      }
      reportUnknownKeys(m, KNOWN_ENTRY_KEYS, where, issues);

      if (!provider || !model) {
        issues.push(`${where}: ต้องมี provider และ model (ข้าม)`);
        return;
      }
      if (!isUnit(m.unit)) {
        issues.push(`${where}: unit "${String(m.unit)}" ไม่รู้จัก (ใช้ได้: ${AI_RATE_UNITS.join(", ")}) (ข้าม)`);
        return;
      }
      const unit = m.unit;

      const dateWindow = parseDateWindow(m, where, issues);
      if (!dateWindow) return;

      const key = rateKeyOf(provider, model);
      const overlaps = (windowsByKey.get(key) || []).some((w) => windowsOverlap(w, dateWindow));
      if (overlaps) {
        issues.push(`${where}: ช่วงวันที่ซ้อนกับรายการก่อนหน้าของ ${key} (ใช้รายการแรก ข้ามรายการนี้)`);
        return;
      }
      if (!windowsByKey.has(key)) windowsByKey.set(key, []);
      windowsByKey.get(key)!.push(dateWindow);

      if (m.prices === undefined && m.free !== true) issues.push(`${where}: ไม่มี prices → ยังไม่ได้ตั้งราคา`);
      const normalized = m.prices === undefined ? { prices: {}, keys: [] } : normalizePrices(m.prices, unit, where, issues);
      const tiers = parseTiers(m.tiers, unit, where, issues);

      let dims: AiRateDim[] | undefined;
      let defaultResolution: AiResolutionBucket | undefined;
      if (unit === "second") {
        dims = reconcileDims(parseDims(m.dims, unit, where, issues), normalized.keys, where, issues);
        defaultResolution = parseDefaultResolution(m.default_resolution, where, issues);
        if (dims.includes("resolution") && !defaultResolution) defaultResolution = "1080P";
      } else {
        parseDims(m.dims, unit, where, issues); // รายงานว่า dims ใช้ไม่ได้กับ unit นี้
        if (m.default_resolution !== undefined) issues.push(`${where}: default_resolution ใช้ได้เฉพาะ unit second (เมิน)`);
      }

      let source: string | undefined;
      if (m.source !== undefined) {
        const s = nonEmptyString(m.source);
        if (s && /^https?:\/\//.test(s)) source = s;
        else issues.push(`${where}: source ต้องเป็น URL ขึ้นต้น http(s) (เมิน)`);
      }
      if (m.free !== undefined && typeof m.free !== "boolean") issues.push(`${where}: free ต้องเป็น true/false`);

      models.push({
        key,
        provider,
        model,
        service: nonEmptyString(m.service) || "",
        unit,
        prices: normalized.prices,
        tiers,
        startDate: dateWindow.startDate,
        endDate: dateWindow.endDate,
        dims,
        defaultResolution,
        free: m.free === true,
        source,
        note: nonEmptyString(m.note) || undefined,
      });
    });
  }

  return { book: { updated, models, providerDefaults }, issues };
};

// ─── Exporter (override ใน browser → JSON วางทับไฟล์) ─────────────────────────

export type PriceBookExtraModel = { provider: string; model: string; service?: string; unit: AiRateUnit };

export type PriceBookExport = {
  text: string; // JSON.stringify(obj, null, 2) + "\n"
  changedModels: number; // model ใน book ที่ราคาเปลี่ยน
  addedModels: number; // model นอก book ที่ถูกเพิ่ม
  changedDefaults: number; // provider_defaults ที่ราคาเปลี่ยน
  skipped: string[]; // override ที่ export ไม่ได้ (variant ไม่ตรง unit ฯลฯ)
};

// prices ตามลำดับ variant ที่ควรมี (ใส่ null เมื่อยังไม่ตั้ง) ตามด้วย key อื่นที่มีอยู่
const orderedPrices = (unit: AiRateUnit, dims: AiRateDim[] | undefined, prices: Record<string, number>): Record<string, number | null> => {
  const out: Record<string, number | null> = {};
  expectedVariantsOf(unit, dims).forEach((v) => {
    out[v] = prices[v] === undefined ? null : prices[v];
  });
  Object.keys(prices)
    .filter((v) => out[v] === undefined)
    .sort((a, b) => variantSortKey(a) - variantSortKey(b))
    .forEach((v) => {
      out[v] = prices[v];
    });
  return out;
};

const mergeOverride = (
  unit: AiRateUnit,
  prices: Record<string, number>,
  ov: Record<string, number> | undefined,
  where: string,
  skipped: string[]
): { prices: Record<string, number>; changed: boolean } => {
  const merged = { ...prices };
  let changed = false;
  Object.entries(ov || {}).forEach(([variant, usd]) => {
    if (!isVariantKeyForUnit(unit, variant)) {
      skipped.push(`${where}: variant "${variant}" ไม่ตรง unit ${unit} (ข้าม)`);
      return;
    }
    if (merged[variant] !== usd) {
      merged[variant] = usd;
      changed = true;
    }
  });
  return { prices: merged, changed };
};

export const exportPriceBookJson = (input: {
  raw: unknown; // JSON ต้นฉบับ — key ที่ขึ้นต้น "_" (เช่น _readme) คัดลอกผ่านไม่แตะ
  book: PriceBook;
  overrides: RateOverrides;
  extraModels?: PriceBookExtraModel[]; // model นอก book (จาก log) — ให้ unit/service ตอนเพิ่ม entry
  today: string; // YYYY-MM-DD
}): PriceBookExport => {
  const { book, overrides, today } = input;
  const extraModels = input.extraModels || [];
  const skipped: string[] = [];
  let changedModels = 0;
  let addedModels = 0;
  let changedDefaults = 0;
  const used = new Set<string>();

  const out: Record<string, unknown> = {};
  if (isRecord(input.raw)) {
    Object.entries(input.raw).forEach(([k, v]) => {
      if (k.startsWith("_")) out[k] = JSON.parse(JSON.stringify(v));
    });
  }

  // provider_defaults
  const defaults: Record<string, unknown> = {};
  Object.values(book.providerDefaults).forEach((def) => {
    const key = providerDefaultKeyOf(def.provider);
    used.add(key);
    const entry: Record<string, unknown> = {};
    if (def.service) entry.service = def.service;
    if (def.unit) entry.unit = def.unit;
    if (def.unit === "second") entry.dims = def.dims || [];
    if (def.unit === "second" && def.defaultResolution && (def.dims || []).includes("resolution")) entry.default_resolution = def.defaultResolution;

    let prices = def.prices;
    if (overrides[key]) {
      if (!def.unit) {
        skipped.push(`${key}: ค่าเริ่มต้นของ ${def.provider} ไม่มี unit (ฟรี) → ไม่ export override`);
      } else {
        const merged = mergeOverride(def.unit, def.prices, overrides[key], key, skipped);
        prices = merged.prices;
        if (merged.changed) changedDefaults += 1;
      }
    }
    if (def.unit) entry.prices = orderedPrices(def.unit, def.dims, prices);
    if (def.free) entry.free = true;
    if (def.note) entry.note = def.note;
    defaults[def.provider] = entry;
  });

  // models ใน book ตามลำดับเดิม
  const bookKeys = new Set(book.models.map((m) => m.key));
  const models: Record<string, unknown>[] = book.models.map((m) => {
    const ekey = entryKeyOf(m); // override ของรายการ dated ผูกกับช่วงวันที่ของมันเอง
    used.add(ekey);
    const merged = mergeOverride(m.unit, m.prices, overrides[ekey], ekey, skipped);
    if (merged.changed) changedModels += 1;

    const entry: Record<string, unknown> = { provider: m.provider, model: m.model };
    if (m.service) entry.service = m.service;
    entry.unit = m.unit;
    if (m.startDate) entry.start_date = m.startDate;
    if (m.endDate) entry.end_date = m.endDate;
    if (m.unit === "second") entry.dims = m.dims || [];
    if (m.unit === "second" && m.defaultResolution && (m.dims || []).includes("resolution")) entry.default_resolution = m.defaultResolution;
    entry.prices = orderedPrices(m.unit, m.dims, merged.prices);
    // tiers เป็นข้อมูลที่แก้จากไฟล์เท่านั้น (หน้าจอแก้ได้แค่ prices บนสุด) — คัดลอกผ่านไม่ให้หายตอนวางทับไฟล์
    if (m.tiers) {
      entry.tiers = m.tiers.map((t) => ({
        ...(t.maxInputTokens !== undefined ? { max_input_tokens: t.maxInputTokens } : {}),
        prices: { ...t.prices },
      }));
    }
    if (m.free) entry.free = true;
    if (m.source) entry.source = m.source;
    if (m.note) entry.note = m.note;
    return entry;
  });

  // model นอก book ที่มี override → entry ใหม่
  Object.entries(overrides)
    .filter(([key]) => !used.has(key))
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .forEach(([key, ov]) => {
      const aliasTarget = usageOverrideTargetOf(key);
      if (aliasTarget) {
        // Do not resurrect a known typo or silently overwrite the canonical rate with a legacy edit.
        // The edit remains in browser storage and the price card explains how to review it.
        skipped.push(`${key} → ${aliasTarget}`);
        return;
      }
      const baseKey = key.split("@")[0]; // ตัด suffix ช่วงวันที่ (รายการใหม่จากหน้าจอยังไม่มีวันที่)
      if (isProviderDefaultKey(baseKey)) {
        skipped.push(`${key}: ไม่มี provider_defaults ของ provider นี้ใน price book (ข้าม)`);
        return;
      }
      // override key เดิม (ไม่มีวันที่) ของ model ที่ตอนนี้มีแต่รายการ dated → ไม่ export เป็นรายการใหม่
      // เพราะ entry ใหม่จะกลายเป็น "ไม่มีวันที่" ที่ซ้อนทุกช่วงและกลืนราคาช่วงจริง (ให้ลบที่การ์ดราคากำพร้าแทน)
      if (bookKeys.has(baseKey)) {
        skipped.push(`${key}: model นี้มีแต่รายการแบบมีวันที่ใน price book → override เดิมไม่ตรงช่วงไหนเลย (ข้าม)`);
        return;
      }
      const [provider, model] = baseKey.split("|");
      const extra = extraModels.find((e) => rateKeyOf(e.provider, e.model) === baseKey);
      const unit = extra?.unit || unitFromVariants(Object.keys(ov));
      if (!unit) {
        skipped.push(`${key}: ไม่ทราบ unit ของ model นี้ → ไม่ export`);
        return;
      }
      const merged = mergeOverride(unit, {}, ov, key, skipped);
      if (!Object.keys(merged.prices).length) return;

      const entry: Record<string, unknown> = { provider: extra?.provider || provider, model: extra?.model || model };
      if (extra?.service) entry.service = extra.service;
      entry.unit = unit;
      const dims = unit === "second" ? dimsFromKeys(Object.keys(merged.prices)) : undefined;
      if (unit === "second") entry.dims = dims;
      entry.prices = orderedPrices(unit, dims, merged.prices);
      entry.note = `เพิ่มจากหน้าจอ MeGen Cost ${today}`;
      models.push(entry);
      addedModels += 1;
    });

  const changed = changedModels + addedModels + changedDefaults > 0;
  out.updated = changed || !book.updated ? today : book.updated;
  out.currency = "USD";
  if (Object.keys(defaults).length) out.provider_defaults = defaults;
  out.models = models;

  return { text: `${JSON.stringify(out, null, 2)}\n`, changedModels, addedModels, changedDefaults, skipped };
};
